import { useEffect, useState } from 'react';
import Table from '../../components/Table/Table';
import RoundCheckBox from '../../components/CustomCheckbox/RoundCheckbox';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import AdminAddingModal from '../../components/AdminAddingModal/AdminAddingModal';
import Select from 'react-select';
import { permissions } from '../../constants/enums/Permissions';
import request from '../../utils/APIHelper/request';
import {
  controllers,
  requestTypes,
  responseTypes,
} from '../../constants/enums/API';
import loadingGif from '../../assets/Other/loading.gif';
import { adminRoles } from '../../constants/enums/AdminRoles';
import { currentUser } from '../../utils/Auth/auth';
import { PlusCircleIcon } from '@heroicons/react/24/outline';

const CustomDropdownIndicator = () => {
  return (
    <PlusCircleIcon className="w-5 h-5 m-2 text-blue-400 hover:text-blue-300" />
  );
};

const Administrators = ({ swalModal }) => {
  const [users, setUsers] = useState([]);
  const [tempUsers, setTempUsers] = useState([]);
  const [loading, setLoading] = useState();

  useEffect(() => {
    const fetchAdmins = async () => {
      const result = await request({
        url: `${controllers.auth}getAdmins`,
        type: requestTypes.get,
        setLoading,
      });
      if (result.code !== responseTypes.error) {
        setUsers(result);
        setTempUsers(result);
      } else {
        toast.error(result.message);
      }
    };

    fetchAdmins();
  }, []);

  const showSwal = () => {
    swalModal.fire({
      title: 'Add new admin',
      html: (
        <AdminAddingModal
          admins={users}
          setAdmins={(val) => {
            setUsers(val);
            setTempUsers(val);
          }}
        />
      ),
      showCancelButton: false,
      showConfirmButton: false,
      customClass: {
        confirmButton: 'bg-blue-800 text-white hover:bg-blue-900',
        cancelButton: 'bg-gray-300 text-gray-800 hover:bg-gray-400',
        popup: 'w-[400px] text-sm',
      },
    });
  };

  const changeFieldValue = (field, value, id) => {
    setTempUsers((prevState) =>
      prevState.map((user) =>
        user.id === id ? { ...user, [field]: value } : user,
      ),
    );
  };

  const permissionsOptions = Object.keys(permissions).map((i) => ({
    value: i,
    label: permissions[i],
  }));

  const branches = [
    { label: '1 - Toronto', value: '1' },
    { label: '2 - Port Reading', value: '2' },
    { label: '3 - California', value: '3' },
    { label: '4 - Chicago', value: '4' },
  ];

  const getUsersBranches = (index) => {
    const branchOptions = tempUsers[index].branches;
    const baseBranch = tempUsers[index].baseBranch;
    const splitted = branchOptions.split(',');
    const existing = splitted.filter((i) =>
      branches.some((x) => x.value === i),
    );
    const res = existing.map((i) => branches.find((x) => x.value === i));

    return res.map((item) => ({
      ...item,
      isFixed: item.value === baseBranch?.toString(),
    }));
  };

  const multipleSelectStyles = {
    multiValue: (base, state) => {
      return state.data.isFixed
        ? { ...base, backgroundColor: '#D0D0D0' }
        : base;
    },
    multiValueLabel: (base, state) => {
      return state.data.isFixed
        ? { ...base, color: '#5E5E5E', paddingRight: 6 }
        : base;
    },
    multiValueRemove: (base, state) => {
      return state.data.isFixed ? { ...base, display: 'none' } : base;
    },
  };

  const onBranchesChange = (newValue, itemId) => {
    let branchesString = newValue?.map((i) => i.value).join(',');
    if (
      !branchesString.includes(
        users.find((x) => x.id === itemId).baseBranch?.toString(),
      )
    ) {
      branchesString += users[itemId].baseBranch?.toString();
    }
    changeFieldValue('branches', branchesString, itemId);
  };
  const createDisplayColumns = (data) => {
    return data.map((item, index) => ({
      Name: (
        <input
          disabled={item.status === adminRoles.master}
          className={classNames(
            'border-b-[1px] border-blue-500 focus:outline-none focus:border-blue-300',
            users[index].name !== item.name
              ? 'text-blue-900 bg-blue-100'
              : null,
            item.status === adminRoles.master
              ? 'bg-gray-200 text-gray-400 border-gray-500 cursor-not-allowed'
              : null,
          )}
          type="text"
          value={item.name}
          onChange={(e) => changeFieldValue('name', e.target.value, item.id)}
          placeholder="Name..."
        />
      ),
      ...(currentUser?.role === 'master' && {
        Admin: (
          <RoundCheckBox
            disabled={item.status === adminRoles.master}
            value={item.administrators}
            previousValue={users[index].administrators}
            changeFieldValue={(value) =>
              changeFieldValue('administrators', value, item.id)
            }
          />
        ),
      }),
      Shipments: (
        <div
          className={classNames(
            item.status === adminRoles.master ? 'cursor-not-allowed' : null,
          )}
        >
          <Select
            isDisabled={item.status === adminRoles.master}
            value={permissionsOptions.find((x) => x.value === item.shipments)}
            onChange={(value) =>
              changeFieldValue('shipments', value.value, item.id)
            }
            options={permissionsOptions}
            styles={{
              control: (provided) => ({
                ...provided,
                border: 'none',
                boxShadow: 'none',
                padding: '0',
                backgroundColor: 'none',
              }),
              indicatorSeparator: () => ({
                display: 'none',
              }),
            }}
            className={classNames(
              'w-40',
              users[index].shipments !== item.shipments ? 'bg-blue-100' : null,
            )}
          />
        </div>
      ),
      'Parcel Rates': (
        <div
          className={classNames(
            item.status === adminRoles.master ? 'cursor-not-allowed' : null,
          )}
        >
          <Select
            isDisabled={item.status === adminRoles.master}
            value={permissionsOptions.find((x) => x.value === item.parcelRates)}
            onChange={(value) =>
              changeFieldValue('parcelRates', value.value, item.id)
            }
            options={permissionsOptions}
            styles={{
              control: (provided) => ({
                ...provided,
                border: 'none',
                boxShadow: 'none',
                padding: '0',
                backgroundColor: 'none',
              }),
              indicatorSeparator: () => ({
                display: 'none',
              }),
            }}
            className={classNames(
              'w-40',
              users[index].parcelRates !== item.parcelRates
                ? 'bg-blue-100'
                : null,
            )}
          />
        </div>
      ),
      'Money Transfers (Shipments tab)': (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.moneyTransfers}
          previousValue={users[index].moneyTransfers}
          changeFieldValue={(value) =>
            changeFieldValue('moneyTransfers', value, item.id)
          }
        />
      ),
      "Change container's branch": (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.containerBranchChange}
          previousValue={users[index].containerBranchChange}
          changeFieldValue={(value) =>
            changeFieldValue('containerBranchChange', value, item.id)
          }
        />
      ),
      Reports: (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.reports}
          previousValue={users[index].reports}
          changeFieldValue={(value) =>
            changeFieldValue('reports', value, item.id)
          }
        />
      ),
      Refund: (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.refund}
          previousValue={users[index].refund}
          changeFieldValue={(value) =>
            changeFieldValue('refund', value, item.id)
          }
        />
      ),
      'Agent list': (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.agentList}
          previousValue={users[index].agentList}
          changeFieldValue={(value) =>
            changeFieldValue('agentList', value, item.id)
          }
        />
      ),
      Users: (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.userAccounts}
          previousValue={users[index].userAccounts}
          changeFieldValue={(value) =>
            changeFieldValue('userAccounts', value, item.id)
          }
        />
      ),
      'CanPar Tracking': (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.canParTracking}
          previousValue={users[index].canParTracking}
          changeFieldValue={(value) =>
            changeFieldValue('canParTracking', value, item.id)
          }
        />
      ),
      'Odoo-Portal Sync': (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.odooSync}
          previousValue={users[index].odooSync}
          changeFieldValue={(value) =>
            changeFieldValue('odooSync', value, item.id)
          }
        />
      ),
      Branches: (
        <div>
          <Select
            className="max-w-56"
            onChange={(value) => onBranchesChange(value, item.id)}
            isMulti={true}
            isClearable={getUsersBranches(index).some((v) => !v.isFixed)}
            options={branches}
            styles={multipleSelectStyles}
            components={{ DropdownIndicator: CustomDropdownIndicator }}
            value={getUsersBranches(index)}
          />
        </div>
      ),
      'Is Active': (
        <RoundCheckBox
          disabled={item.status === adminRoles.master}
          value={item.isActive}
          previousValue={users[index].isActive}
          changeFieldValue={(value) =>
            changeFieldValue('isActive', value, item.id)
          }
        />
      ),
    }));
  };

  const update = async () => {
    if (JSON.stringify(users) !== JSON.stringify(tempUsers)) {
      const result = await request({
        url: `${controllers.auth}updateAdmins`,
        body: tempUsers,
        type: requestTypes.post,
        setLoading,
      });
      if (result.code !== responseTypes.error) {
        setUsers(tempUsers);
        toast.info('Updated administrators permissions!');
      } else {
        toast.info(result.message);
      }
    }
  };

  return (
    <div>
      <div className="text-lg text-blue-800">MEEST HELPER USERS</div>
      {loading ? (
        <img src={loadingGif} className="w-6 h-6 my-10 mx-auto" />
      ) : (
        <div>
          <div className="text-left">
            <div
              onClick={() => setTempUsers(users)}
              className={classNames(
                'inline-block text-white px-4 rounded-[20px]',
                JSON.stringify(users) === JSON.stringify(tempUsers)
                  ? 'bg-gray-400'
                  : 'bg-blue-900 cursor-pointer hover:bg-blue-800 hover:transition-all hover:duration-300',
              )}
            >
              Clear choice
            </div>
          </div>
          <Table
            stickyHeaders={true}
            containerClassName="!overflow-x-none pb-[10rem]"
            rowsClassName="text-[1.5vw] md:text-[1vw] font-semibold xl:font-normal xl:text-xs"
            headersClassNames="text-[1.5vw] md:text-[1vw] xl:font-normal xl:text-xs"
            data={createDisplayColumns(tempUsers)}
          />
          <div className="text-left text-blue-900 p-4 bg-none">
            Add new admin
            <div
              onClick={showSwal}
              className="cursor-pointer bg-blue-900 text-white rounded-full h-6 w-6 inline-flex items-center justify-center pb-[2px] mx-2 hover:bg-blue-800 hover:transition-all hover:duration-300"
            >
              +
            </div>
          </div>
          <div className="text-right mb-10">
            <div
              onClick={update}
              className={classNames(
                'text-white inline-block mx-4 px-3 pb-[2px] rounded-[20px]',
                JSON.stringify(users) === JSON.stringify(tempUsers)
                  ? 'bg-gray-400'
                  : 'bg-red-800 cursor-pointer hover:bg-red-900 hover:transition-all hover:duration-300',
              )}
            >
              Save Changes
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Administrators;
